import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaEnvelope, FaArrowLeft } from 'react-icons/fa';
import { fetchWithAuth } from '../services/api';

const ForgotPassword = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setIsSubmitting(true);

    try {
      const response = await fetchWithAuth('/auth/forgot-password', {
        method: 'POST',
        body: { email },
      });

      if (response.success) {
        setIsSuccess(true);
        setMessage(response.message || t('forgotPassword.successMessage'));
        setEmail('');
      } else {
        setError(response.message || t('forgotPassword.error'));
      }
    } catch (err) {
      if (err.message && err.message.includes('once per 24 hours')) {
        setError(t('forgotPassword.rateLimitError'));
      } else {
        setError(err.message || t('forgotPassword.networkError'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex flex-col justify-center py-12 sm:px-6 lg:px-8 bg-gray-50">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          {t('forgotPassword.title', 'Forgot Password')}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          {t('forgotPassword.subtitle', 'Enter your email address and we\'ll send you instructions to reset your password.')}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {isSuccess ? (
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <FaEnvelope className="h-6 w-6 text-green-600" />
              </div>
              <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md mb-4">
                <p className="text-sm">{message}</p>
              </div>
              <p className="text-sm text-gray-600 mb-6">
                {t('forgotPassword.checkEmail', 'Please check your email inbox and spam folder.')}
              </p>
              <Link
                to={`/${window.location.pathname.split('/')[1] || 'mk'}/login`}
                className="inline-flex items-center text-blue-600 hover:text-blue-500 font-medium"
              >
                <FaArrowLeft className="mr-2" />
                {t('forgotPassword.backToLogin', 'Back to Login')}
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md">
                  <p className="text-sm">{error}</p>
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  {t('forgotPassword.emailLabel', 'Email Address')}
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaEnvelope className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder={t('forgotPassword.emailPlaceholder', 'you@example.com')}
                  />
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? t('forgotPassword.sending', 'Sending...') : t('forgotPassword.sendButton', 'Send Reset Link')}
                </button>
              </div>

              <div className="flex items-center justify-center">
                <Link
                  to={`/${window.location.pathname.split('/')[1] || 'mk'}/login`}
                  className="inline-flex items-center text-sm text-blue-600 hover:text-blue-500 font-medium"
                >
                  <FaArrowLeft className="mr-2" />
                  {t('forgotPassword.backToLogin', 'Back to Login')}
                </Link>
              </div>
            </form>
          )}
        </div>

        <div className="mt-6">
          <div className="relative">
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-gray-50 text-gray-500">
                {t('forgotPassword.securityNote', 'For security reasons, you can only request a password reset once every 24 hours.')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
